import moment from "moment";
import parsePhoneNumberFromString from "libphonenumber-js";

export const formatInvoicePhone = (value) => {
    if (!value) return "—";
    const parsed = parsePhoneNumberFromString(`+${String(value).replace(/^\+/, "")}`);
    return parsed?.formatNational?.() || value;
};

export const formatInvoiceDateTime = (value) => (value ? moment(value).format("MMM D, YYYY h:mm A") : "—");

const PROVIDER_LABELS = {
    teqmobility: "TeqMobility",
    bonzah: "Bonzah",
    abi: "INSHUR",
    axle: "Axle",
};

const isAddonType = (type) => ["teqmobility", "bonzah", "abi"].includes(String(type || "").toLowerCase());

const getAddonRecords = (data) => (data ? (Array.isArray(data) ? data : [data]) : []);

export const buildInsuranceInvoiceSection = (resDetails, insurancePolicyData) => {
    const insuranceType = resDetails?.insuranceType;
    if (!insuranceType) return { hasInsurance: false };

    const key = String(insuranceType).toLowerCase();
    const providerLabel = PROVIDER_LABELS[key] || insuranceType.charAt(0).toUpperCase() + insuranceType.slice(1);

    if (isAddonType(insuranceType)) {
        const addons = getAddonRecords(insurancePolicyData);
        const addonSections = addons
            .filter((a) => a?.name && Array.isArray(a.featureList))
            .map((addon) => ({
                addonName: addon.name,
                lines: addon.featureList.filter((f) => f.isActive && f.text).map((f) => ({ label: f.text })),
            }))
            .filter((s) => s.lines.length > 0);

        return {
            hasInsurance: true,
            providerLabel,
            policyNumber: addons[0]?.policyNumber || addons[0]?.policyId || null,
            carrier: null,
            coverageLines: [],
            addonSections,
        };
    }

    const coverageLines = (insurancePolicyData?.coverages || []).map((c) => ({
        label: c.label || "Coverage",
        detail: c.limitPerPerson
            ? `$${c.limitPerPerson.toLocaleString()} per person`
            : c.limitPerAccident
              ? `$${c.limitPerAccident.toLocaleString()} per accident`
              : c.deductible
                ? `$${c.deductible} deductible`
                : "Covered",
    }));

    return {
        hasInsurance: true,
        providerLabel,
        policyNumber: insurancePolicyData?.policyNumber || null,
        carrier: insurancePolicyData?.carrier || null,
        coverageLines,
        addonSections: [],
    };
};

export const PAYMENT_EVENT_STATUS_CONFIG = {
    success: { color: "success", printColor: "#389e0d" },
    failed: { color: "error", printColor: "#cf1322" },
    pending: { color: "warning", printColor: "#d48806" },
};

export const normalizePaymentEventStatus = (status) => {
    const normalized = String(status ?? "").toLowerCase();
    return Object.prototype.hasOwnProperty.call(PAYMENT_EVENT_STATUS_CONFIG, normalized)
        ? normalized
        : "pending";
};

export const normalizePaymentEvents = (invoice) => {
    const raw = invoice?.paymentEvents ?? invoice?.paymentEvent;
    const events = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
    return events.map((e, idx) => ({
        id: e.paymentIntentId ?? e.refundId ?? e.sessionId ?? `event-${idx}`,
        eventType: e.eventType ?? "checkout",
        amount: Number(e.amount ?? 0),
        status: normalizePaymentEventStatus(e.status),
        paymentPlatform: e.paymentPlatform ?? "",
        paymentIntentId: e.paymentIntentId ?? "",
        refundId: e.refundId ?? "",
        reservationProcess: e.reservationProcess ?? "",
        description: e.description ?? "",
        updatedAt: e.updatedAt ?? e.createdAt ?? null,
    }));
};

export const capitalizeChargeDescription = (description) => {
    if (!description) return "";
    return description.charAt(0).toUpperCase() + description.slice(1).toLowerCase();
};
